import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  FiPlus,
  FiSearch,
  FiX,
  FiCheckCircle,
  FiAlertTriangle,
  FiAlertCircle,
  FiChevronDown,
} from "react-icons/fi";
import { getAuthHeaders } from "../utils/getAuthHeaders";
import { getErrorMessage } from "../utils/getErrorMessage";
import { endpoints } from "../config/api";
import { fetchAllOrgUsers, flattenOrgUsersForPicker } from "../utils/fetchOrgUsers";
import {
  isAlreadyAdded,
  isValidEmail,
  normalizeEmail,
  parseBulkAddResponse,
  parseEmailList,
  groupUsersByOrg,
} from "../utils/forwardingAccounts";
import Toast from "./Toast";

const DEV_BRANCH_MSG =
  "A different branch is deployed in the backend for testing — please reach out to the backend team.";

function apiError(error, fallback) {
  if (error?.response?.status === 404) return DEV_BRANCH_MSG;
  return getErrorMessage(error, fallback);
}

const jsonHeaders = () => ({
  ...getAuthHeaders(),
  Accept: "*/*",
  "Content-Type": "application/json",
});

function userTitle(user) {
  return user.name || user.email || `User ${user.userIndex}`;
}

/**
 * Searchable user dropdown grouped by organization. Opens on focus with every
 * user, narrows as the admin types, and calls `onPick` with the chosen user.
 * With `onCustomEmail`, a typed address that is a valid email can be used as-is.
 */
const UserDropdown = ({ id, users, onPick, onCustomEmail, placeholder, disabled, emptyText }) => {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);
  const listRef = useRef(null);

  const groups = useMemo(() => groupUsersByOrg(users, search), [users, search]);
  // Flat list in display order, so arrow keys walk across org boundaries.
  const options = useMemo(() => groups.flatMap((g) => g.users), [groups]);

  const typedEmail = normalizeEmail(search);
  const customEmail =
    onCustomEmail &&
    typedEmail &&
    isValidEmail(typedEmail) &&
    !users.some((u) => normalizeEmail(u.email) === typedEmail)
      ? typedEmail
      : null;

  useEffect(() => {
    setActive(0);
  }, [search]);

  // Keep the keyboard-highlighted row visible in the scrolling list.
  useEffect(() => {
    if (!open || !listRef.current) return;
    listRef.current
      .querySelector(`[data-index="${active}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  const pick = (user) => {
    onPick(user);
    setSearch("");
  };

  const applyCustomEmail = () => {
    onCustomEmail(customEmail);
    setSearch("");
    setOpen(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      setOpen(false);
      return;
    }
    if (event.key === "Enter" && customEmail && !options.length) {
      event.preventDefault();
      applyCustomEmail();
      return;
    }
    if (!options.length) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActive((i) => (i + 1) % options.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      setActive((i) => (i - 1 + options.length) % options.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const user = options[active] || options[0];
      if (user) pick(user);
    }
  };

  const listId = `${id}-options`;

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <div style={searchWrapStyle}>
        <FiSearch aria-hidden="true" style={searchIconStyle} />
        <input
          id={id}
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          style={{ ...searchInputStyle, ...(disabled ? disabledInputStyle : null) }}
        />
        <FiChevronDown aria-hidden="true" style={chevronStyle} />
      </div>

      {open && !disabled && (
        <ul id={listId} role="listbox" ref={listRef} style={suggestionListStyle}>
          {customEmail && (
            <li role="option" aria-selected={false}>
              <button type="button" onClick={applyCustomEmail} style={{ ...userRowStyle, ...customRowStyle }}>
                <span style={userNameStyle}>Use “{customEmail}”</span>
                <span style={userMetaStyle}>Any valid email works — it doesn't need a Munshot account</span>
              </button>
            </li>
          )}
          {options.length === 0 ? (
            !customEmail && (
              <li style={emptyStyle}>{search.trim() ? "No users match that search." : emptyText}</li>
            )
          ) : (
            groups.map((group) => (
              <li key={group.orgName} role="presentation">
                <div style={groupHeaderStyle}>
                  {group.orgName}
                  <span style={groupCountStyle}>{group.users.length}</span>
                </div>
                <ul role="group" aria-label={group.orgName} style={groupListStyle}>
                  {group.users.map((user) => {
                    const index = options.indexOf(user);
                    return (
                      <li key={user.userIndex} role="option" aria-selected={index === active}>
                        <button
                          type="button"
                          data-index={index}
                          onMouseEnter={() => setActive(index)}
                          onClick={() => pick(user)}
                          style={{ ...userRowStyle, ...(index === active ? userRowActiveStyle : null) }}
                        >
                          <span style={userNameStyle}>{userTitle(user)}</span>
                          {user.name && user.email && <span style={userMetaStyle}>{user.email}</span>}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};

/**
 * Share one mailbox (e.g. a CEO's) with many users in a single
 * POST /email/forwarding-accounts call, then show the per-user outcome.
 */
const OrgForwardingAccounts = () => {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  const [ceoIndex, setCeoIndex] = useState(null);
  /** CEO mailbox typed by hand — the source address need not be a Munshot user. */
  const [ceoCustomEmail, setCeoCustomEmail] = useState(null);
  const [label, setLabel] = useState("");
  const [pastedEmails, setPastedEmails] = useState("");
  const [selectedIndexes, setSelectedIndexes] = useState([]);

  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((type, message) => {
    setToast({ id: Date.now(), type, message });
  }, []);
  const dismissToast = useCallback(() => setToast(null), []);

  useEffect(() => {
    let cancelled = false;
    const loadUsers = async () => {
      setLoadingUsers(true);
      try {
        const orgGroups = await fetchAllOrgUsers(endpoints.orgsUsers);
        if (!cancelled) setUsers(flattenOrgUsersForPicker(orgGroups));
      } catch (error) {
        if (cancelled) return;
        console.error("Error fetching org users", error);
        showToast("error", apiError(error, "Failed to fetch users."));
      } finally {
        if (!cancelled) setLoadingUsers(false);
      }
    };
    loadUsers();
    return () => {
      cancelled = true;
    };
  }, [showToast]);

  const usersByIndex = useMemo(() => new Map(users.map((u) => [u.userIndex, u])), [users]);
  const ceo = ceoIndex != null ? usersByIndex.get(ceoIndex) || null : null;

  const ceoEmail = ceo ? normalizeEmail(ceo.email) : ceoCustomEmail;
  const hasCeo = Boolean(ceo || ceoCustomEmail);

  /**
   * Recipients the dropdown offers: everyone else in a registered CEO's org, or
   * every user (grouped by org) when the CEO mailbox is an external address.
   */
  const orgUsers = useMemo(() => {
    if (ceo) {
      return users.filter(
        (u) => u.organizationId === ceo.organizationId && u.userIndex !== ceo.userIndex
      );
    }
    return ceoCustomEmail ? users : [];
  }, [users, ceo, ceoCustomEmail]);

  const availableOrgUsers = useMemo(
    () => orgUsers.filter((u) => !selectedIndexes.includes(u.userIndex)),
    [orgUsers, selectedIndexes]
  );

  const parsedEmails = useMemo(() => parseEmailList(pastedEmails), [pastedEmails]);

  const clearCeo = () => {
    setCeoIndex(null);
    setCeoCustomEmail(null);
  };

  const pickCustomCeo = (email) => {
    setCeoIndex(null);
    setCeoCustomEmail(email);
    setSelectedIndexes([]);
    setOutcome(null);
  };

  const pickCeo = (user) => {
    setCeoCustomEmail(null);
    setCeoIndex(user.userIndex);
    // Recipients belong to the previous CEO's org, so start the list over.
    setSelectedIndexes([]);
    setOutcome(null);
  };

  const addUser = useCallback((user) => {
    setSelectedIndexes((prev) => (prev.includes(user.userIndex) ? prev : [...prev, user.userIndex]));
  }, []);

  const removeUser = (userIndex) => {
    setSelectedIndexes((prev) => prev.filter((i) => i !== userIndex));
  };

  const addAllOrgUsers = () => {
    setSelectedIndexes(orgUsers.map((u) => u.userIndex));
  };

  const describeUser = (userIndex) => {
    const user = usersByIndex.get(userIndex);
    if (!user) return userIndex != null ? `User ${userIndex}` : "User";
    return user.name ? `${user.name} (${user.email || user.userIndex})` : user.email || `User ${userIndex}`;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const email = ceoEmail;
    if (!email || !isValidEmail(email)) {
      showToast("error", hasCeo ? "The selected CEO has no valid email." : "Select the CEO first.");
      return;
    }
    if (parsedEmails.invalid.length) {
      showToast("error", `Fix invalid emails: ${parsedEmails.invalid.join(", ")}`);
      return;
    }
    if (!selectedIndexes.length && !parsedEmails.valid.length) {
      showToast("error", "Pick at least one user or paste at least one user email.");
      return;
    }

    setSubmitting(true);
    setOutcome(null);
    try {
      const response = await axios.post(
        endpoints.emailForwardingAccounts,
        {
          account_email: email,
          ...(selectedIndexes.length ? { user_indexes: selectedIndexes } : {}),
          ...(parsedEmails.valid.length ? { user_emails: parsedEmails.valid } : {}),
          ...(label.trim() ? { label: label.trim() } : {}),
        },
        { headers: jsonHeaders() }
      );
      const parsed = parseBulkAddResponse(response.data);
      setOutcome({ accountEmail: email, ...parsed });

      const added = parsed.results.filter((r) => r?.status === "added").length;
      const already = parsed.results.filter(isAlreadyAdded).length;
      const failed = parsed.results.length - added - already;
      const parts = [
        added && `shared with ${added} user${added === 1 ? "" : "s"}`,
        already && `${already} already had it`,
        failed && `${failed} skipped`,
        parsed.notFound.length && `${parsed.notFound.length} not found`,
      ].filter(Boolean);
      showToast(
        added || already ? "success" : "error",
        `${email}: ${parts.join(", ") || "no users were processed"}.`
      );
      if (added || already) {
        setSelectedIndexes([]);
        setPastedEmails("");
      }
    } catch (error) {
      console.error("Error adding org forwarding account", error);
      showToast("error", apiError(error, "Failed to add forwarding account."));
    } finally {
      setSubmitting(false);
    }
  };

  const targetCount = selectedIndexes.length + parsedEmails.valid.length;

  return (
    <div>
      <form onSubmit={handleSubmit} style={formStyle}>
        <div style={rowStyle}>
          <div style={{ flex: 2, minWidth: 240 }}>
            <label htmlFor="ofa-ceo" style={labelStyle}>
              CEO
            </label>
            {hasCeo ? (
              <div style={selectedChipStyle}>
                <div style={{ minWidth: 0 }}>
                  <span style={userNameStyle}>{ceo ? userTitle(ceo) : ceoCustomEmail}</span>
                  <span style={userMetaStyle}>
                    {ceo
                      ? [ceo.email, ceo.orgName].filter(Boolean).join(" · ") || "—"
                      : "External email · not a Munshot user"}
                  </span>
                </div>
                <button type="button" onClick={clearCeo} style={ghostButtonStyle}>
                  <FiX aria-hidden="true" />
                  Change
                </button>
              </div>
            ) : (
              <UserDropdown
                id="ofa-ceo"
                users={users}
                onPick={pickCeo}
                onCustomEmail={pickCustomCeo}
                placeholder={
                  loadingUsers ? "Loading users…" : "Select the CEO or type any email address"
                }
                disabled={loadingUsers}
                emptyText="No users found."
              />
            )}
          </div>
          <div style={{ flex: 2, minWidth: 200 }}>
            <label htmlFor="ofa-label" style={labelStyle}>
              Label (optional)
            </label>
            <input
              id="ofa-label"
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="CEO reports shared with team"
              maxLength={255}
              style={inputStyle}
            />
          </div>
        </div>

        <div>
          <div style={recipientHeaderStyle}>
            <label htmlFor="ofa-user-search" style={{ ...labelStyle, marginBottom: 0 }}>
              Users who should also receive these emails
              {ceo && <span style={labelMetaStyle}> · {ceo.orgName}</span>}
            </label>
            {ceo && availableOrgUsers.length > 0 && (
              <button type="button" onClick={addAllOrgUsers} style={ghostButtonStyle}>
                <FiPlus aria-hidden="true" />
                Add all {orgUsers.length}
              </button>
            )}
          </div>
          <UserDropdown
            id="ofa-user-search"
            users={availableOrgUsers}
            onPick={addUser}
            placeholder={
              !hasCeo
                ? "Select the CEO first"
                : ceo
                ? `Add users from ${ceo.orgName || "the CEO's organization"}`
                : "Add users — search by name, email or org"
            }
            disabled={!hasCeo}
            emptyText={
              orgUsers.length
                ? "Every user in this organization is already added."
                : "No other users in this organization."
            }
          />

          {selectedIndexes.length > 0 && (
            <div style={chipListStyle}>
              {selectedIndexes.map((userIndex) => (
                <span key={userIndex} style={chipStyle}>
                  {describeUser(userIndex)}
                  <button
                    type="button"
                    onClick={() => removeUser(userIndex)}
                    style={chipRemoveStyle}
                    aria-label={`Remove ${describeUser(userIndex)}`}
                  >
                    <FiX aria-hidden="true" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div>
          <label htmlFor="ofa-emails" style={labelStyle}>
            Or paste user emails (comma or newline separated)
          </label>
          <textarea
            id="ofa-emails"
            value={pastedEmails}
            onChange={(e) => setPastedEmails(e.target.value)}
            placeholder={"emp1@company.com\nemp2@company.com"}
            rows={4}
            style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }}
          />
          {parsedEmails.invalid.length > 0 && (
            <p style={{ ...hintStyle, color: "#b91c1c", margin: "6px 0 0" }}>
              Invalid: {parsedEmails.invalid.join(", ")}
            </p>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <button
            type="submit"
            disabled={submitting}
            style={{
              ...buttonStyle,
              opacity: submitting ? 0.6 : 1,
              cursor: submitting ? "not-allowed" : "pointer",
            }}
          >
            <FiPlus aria-hidden="true" />
            {submitting ? "Sharing…" : "Share CEO mailbox"}
          </button>
          <span style={hintStyle}>
            {targetCount} user{targetCount === 1 ? "" : "s"} selected
          </span>
        </div>
      </form>

      {outcome && (
        <section style={resultPaneStyle} aria-live="polite">
          <h3 style={sectionTitleStyle}>Result for {outcome.accountEmail}</h3>

          {outcome.results.length === 0 ? (
            <p style={hintStyle}>No users were processed.</p>
          ) : (
            <ul style={resultListStyle}>
              {outcome.results.map((result, i) => {
                const added = result?.status === "added";
                const already = isAlreadyAdded(result);
                const ok = added || already;
                return (
                  <li key={`${result?.user_index ?? "r"}-${i}`} style={resultRowStyle}>
                    {ok ? (
                      <FiCheckCircle aria-hidden="true" style={{ color: "#15803d", flexShrink: 0 }} />
                    ) : (
                      <FiAlertTriangle aria-hidden="true" style={{ color: "#b45309", flexShrink: 0 }} />
                    )}
                    <span style={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}>
                      {describeUser(result?.user_index)}
                    </span>
                    <span style={ok ? addedBadgeStyle : skippedBadgeStyle}>
                      {added ? "Added" : already ? "Already added" : result?.status || "Skipped"}
                    </span>
                    {!ok && result?.reason && (
                      <span style={{ ...hintStyle, flexBasis: "100%", paddingLeft: 26 }}>
                        {result.reason}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {outcome.notFound.length > 0 && (
            <div style={notFoundStyle} role="alert">
              <FiAlertCircle aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <strong>These didn't match any Munshot user</strong>
                <p style={{ margin: "4px 0 0" }}>{outcome.notFound.join(", ")}</p>
              </div>
            </div>
          )}
        </section>
      )}

      <Toast toast={toast} onDismiss={dismissToast} />
    </div>
  );
};

const formStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  padding: "24px",
  borderRadius: "8px",
  border: "1px solid #c7d2fe",
  backgroundColor: "#fafbff",
  boxSizing: "border-box",
};

const rowStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: "16px",
};

const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  fontSize: "14px",
  backgroundColor: "#ffffff",
  border: "1px solid #3730a3",
  borderRadius: "6px",
  boxSizing: "border-box",
  color: "#1f2937",
  outline: "none",
};

const labelStyle = {
  display: "block",
  marginBottom: "8px",
  fontSize: "14px",
  fontWeight: "600",
  color: "#3730a3",
};

const hintStyle = {
  color: "#6b7280",
  fontSize: "14px",
};

const searchWrapStyle = {
  position: "relative",
};

const searchIconStyle = {
  position: "absolute",
  left: "12px",
  top: "50%",
  transform: "translateY(-50%)",
  color: "#6b7280",
  pointerEvents: "none",
};

const searchInputStyle = {
  ...inputStyle,
  padding: "10px 36px 10px 36px",
};

const disabledInputStyle = {
  backgroundColor: "#f3f4f6",
  borderColor: "#d1d5db",
  cursor: "not-allowed",
};

const chevronStyle = {
  position: "absolute",
  right: "12px",
  top: "50%",
  transform: "translateY(-50%)",
  color: "#6b7280",
  pointerEvents: "none",
};

const selectedChipStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  padding: "8px 8px 8px 14px",
  borderRadius: "6px",
  border: "1px solid #3730a3",
  backgroundColor: "#ffffff",
  boxSizing: "border-box",
};

const ghostButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  padding: "8px 12px",
  fontSize: "13px",
  fontWeight: "600",
  backgroundColor: "#ffffff",
  color: "#3730a3",
  border: "1px solid #c7d2fe",
  borderRadius: "6px",
  cursor: "pointer",
  flexShrink: 0,
};

const recipientHeaderStyle = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "8px",
  marginBottom: "8px",
};

const labelMetaStyle = {
  fontWeight: "500",
  color: "#6b7280",
};

const suggestionListStyle = {
  position: "absolute",
  top: "calc(100% + 4px)",
  left: 0,
  right: 0,
  zIndex: 20,
  margin: 0,
  padding: 0,
  listStyle: "none",
  maxHeight: "420px",
  overflowY: "auto",
  backgroundColor: "#ffffff",
  border: "1px solid #c7d2fe",
  borderRadius: "6px",
  boxShadow: "0 8px 20px rgba(17, 24, 39, 0.12)",
};

const userRowStyle = {
  display: "block",
  width: "100%",
  textAlign: "left",
  padding: "10px 16px",
  border: "none",
  borderBottom: "1px solid #eef2ff",
  borderLeft: "3px solid transparent",
  backgroundColor: "transparent",
  cursor: "pointer",
  boxSizing: "border-box",
};

const customRowStyle = {
  backgroundColor: "#f5f7ff",
  borderLeftColor: "#3730a3",
};

const groupHeaderStyle = {
  position: "sticky",
  top: 0,
  zIndex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "8px",
  padding: "8px 16px",
  backgroundColor: "#eef2ff",
  color: "#3730a3",
  fontSize: "12px",
  fontWeight: "700",
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  borderBottom: "1px solid #c7d2fe",
};

const groupCountStyle = {
  fontWeight: "600",
  color: "#6b7280",
};

const groupListStyle = {
  listStyle: "none",
  margin: 0,
  padding: 0,
};

const userRowActiveStyle = {
  backgroundColor: "#ffffff",
  borderLeftColor: "#3730a3",
};

const userNameStyle = {
  display: "block",
  fontSize: "14px",
  fontWeight: "600",
  color: "#1f2937",
  wordBreak: "break-word",
};

const userMetaStyle = {
  display: "block",
  fontSize: "12px",
  color: "#6b7280",
  wordBreak: "break-word",
};

const emptyStyle = {
  padding: "16px",
  color: "#6b7280",
  fontSize: "14px",
};

const chipListStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
  marginTop: "12px",
};

const chipStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  padding: "6px 8px 6px 12px",
  borderRadius: "6px",
  backgroundColor: "#e0e7ff",
  color: "#3730a3",
  fontSize: "13px",
  fontWeight: "600",
  maxWidth: "100%",
  wordBreak: "break-word",
};

const chipRemoveStyle = {
  display: "inline-flex",
  alignItems: "center",
  padding: "2px",
  border: "none",
  background: "transparent",
  color: "#3730a3",
  cursor: "pointer",
};

const buttonStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  padding: "12px 24px",
  fontSize: "14px",
  backgroundColor: "#3730a3",
  color: "#ffffff",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
  fontWeight: "600",
};

const resultPaneStyle = {
  marginTop: "20px",
  padding: "24px",
  borderRadius: "8px",
  border: "1px solid #c7d2fe",
  backgroundColor: "#ffffff",
  boxSizing: "border-box",
};

const sectionTitleStyle = {
  color: "#3730a3",
  fontSize: "18px",
  fontWeight: "700",
  marginTop: 0,
  marginBottom: "16px",
  wordBreak: "break-word",
};

const resultListStyle = {
  listStyle: "none",
  margin: 0,
  padding: 0,
  display: "flex",
  flexDirection: "column",
  gap: "8px",
};

const resultRowStyle = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: "10px",
  padding: "10px 12px",
  border: "1px solid #e5e7eb",
  borderRadius: "6px",
  fontSize: "14px",
  color: "#1f2937",
};

const badgeBase = {
  display: "inline-block",
  padding: "4px 10px",
  borderRadius: "6px",
  fontSize: "12px",
  fontWeight: "700",
  textTransform: "capitalize",
};

const addedBadgeStyle = {
  ...badgeBase,
  backgroundColor: "#dcfce7",
  color: "#15803d",
};

const skippedBadgeStyle = {
  ...badgeBase,
  backgroundColor: "#fef3c7",
  color: "#b45309",
};

const notFoundStyle = {
  display: "flex",
  gap: "10px",
  marginTop: "16px",
  padding: "12px 14px",
  borderRadius: "6px",
  border: "1px solid #fecaca",
  backgroundColor: "#fef2f2",
  color: "#b91c1c",
  fontSize: "14px",
  wordBreak: "break-word",
};

export default OrgForwardingAccounts;
